import React, { useState, useEffect, Fragment } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getCurrencySymbol } from '../utils/currencyUtils';
import { InvoiceService } from '../services/InvoiceService';
import { toast } from 'react-hot-toast';

export default function InvoicePreview({ invoiceData, invoiceId, onUpdateInvoice }) {
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    professionalTitle: '',
    email: '',
    phone: '',
    location: ''
  });
  const [customizedData, setCustomizedData] = useState({
    fromName: '',
    clientName: ''
  });
  const [isEditingFrom, setIsEditingFrom] = useState(false);
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInvoiceOwner, setIsInvoiceOwner] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [isHoveringMarkPaid, setIsHoveringMarkPaid] = useState(false);

  // Check authentication and permissions
  useEffect(() => {
    const checkAuth = () => {
      const user = auth.currentUser;
      setIsAuthenticated(!!user);
      setIsInvoiceOwner(user && invoiceData?.userId === user.uid);
    };

    checkAuth();
    
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(checkAuth);
    return () => unsubscribe();
  }, [invoiceData?.userId]);

  // Fetch business information and set initial data
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      console.log('🔍 Fetching business info. Auth state:', !!auth.currentUser);
      console.log('📄 Invoice data businessInfo:', invoiceData?.businessInfo);
      
      // First, always try to use stored business info from invoice
      if (invoiceData?.businessInfo) {
        console.log('✅ Using stored business info from invoice');
        setBusinessInfo({
          businessName: invoiceData.businessInfo.businessName || '',
          professionalTitle: invoiceData.businessInfo.professionalTitle || '',
          email: invoiceData.businessInfo.email || '',
          phone: invoiceData.businessInfo.phone || '',
          location: invoiceData.businessInfo.location || ''
        });
        
        setCustomizedData({
          fromName: invoiceData.fromName || invoiceData.businessInfo.businessName || '',
          clientName: invoiceData.clientName || invoiceData.client || invoiceData.customer || ''
        });
        return;
      }
      
      // If no stored business info and user is authenticated, fetch from profile
      if (auth.currentUser) {
        console.log('👤 Fetching from user profile for authenticated user');
        try {
          const profileRef = doc(db, 'profiles', auth.currentUser.uid);
          const profileDoc = await getDoc(profileRef);
          
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            setBusinessInfo({
              businessName: profileData.name || profileData.businessName || '',
              professionalTitle: profileData.professionalTitle || '',
              email: profileData.contactInfo?.email || auth.currentUser.email || '',
              phone: profileData.contactInfo?.phone || '',
              location: profileData.contactInfo?.location || ''
            });
            
            setCustomizedData({
              fromName: invoiceData?.fromName || profileData.name || profileData.businessName || '',
              clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
            });
          } else {
            console.log('⚠️ No profile found for authenticated user');
            setCustomizedData({
              fromName: invoiceData?.fromName || '',
              clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setCustomizedData({
            fromName: invoiceData?.fromName || '',
            clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
          });
        }
      } else {
        // Unauthenticated user - try to fetch from invoice creator's profile as fallback
        console.log('🔓 Unauthenticated user, trying to fetch creator profile');
        if (invoiceData?.userId) {
          try {
            const profileRef = doc(db, 'profiles', invoiceData.userId);
            const profileDoc = await getDoc(profileRef);
            
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              console.log('✅ Found creator profile data');
              setBusinessInfo({
                businessName: profileData.name || profileData.businessName || '',
                professionalTitle: profileData.professionalTitle || '',
                email: profileData.contactInfo?.email || '',
                phone: profileData.contactInfo?.phone || '',
                location: profileData.contactInfo?.location || ''
              });
              
              setCustomizedData({
                fromName: invoiceData?.fromName || profileData.name || profileData.businessName || '',
                clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
              });
            } else {
              console.log('⚠️ No creator profile found');
              setCustomizedData({
                fromName: invoiceData?.fromName || '',
                clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
              });
            }
          } catch (error) {
            console.error('Error fetching creator profile:', error);
            setCustomizedData({
              fromName: invoiceData?.fromName || '',
              clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
            });
          }
        } else {
          console.log('⚠️ No userId in invoice data');
          setCustomizedData({
            fromName: invoiceData?.fromName || '',
            clientName: invoiceData?.clientName || invoiceData?.client || invoiceData?.customer || ''
          });
        }
      }
    };

    if (invoiceData) {
      fetchBusinessInfo();
    }
  }, [invoiceData, isAuthenticated]);



  // Update customized data and notify parent
  const handleCustomizationChange = (field, value) => {
    setCustomizedData(prev => ({ ...prev, [field]: value }));
  };

  // Save customization when editing is finished
  const handleSaveCustomization = async (field, value) => {
    if (!onUpdateInvoice || !isInvoiceOwner) return;
    
    setIsSaving(true);
    try {
      const newCustomizedData = { ...customizedData, [field]: value };
      setCustomizedData(newCustomizedData);
      
      // Notify parent component to update invoice data
      await onUpdateInvoice({
        ...invoiceData,
        fromName: newCustomizedData.fromName,
        clientName: newCustomizedData.clientName
      });
      
      console.log('Customization saved successfully');
    } catch (error) {
      console.error('Error saving customization:', error);
      // Revert the change if save failed
      setCustomizedData(prev => ({ ...prev, [field]: customizedData[field] }));
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for consistent display
  const formatDate = (date) => {
    if (!date) return 'Not specified';
    if (typeof date === 'string') {
      const d = new Date(date);
      return !isNaN(d) ? d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : 'Invalid date';
    }
    if (date instanceof Date && !isNaN(date)) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    return 'Invalid date';
  };

  // Handle marking invoice as paid manually
  const handleMarkAsPaid = async (paymentMethod = 'Cash') => {
    if (!invoiceId || !isInvoiceOwner) return;
    
    setIsMarkingPaid(true);
    try {
      await InvoiceService.markAsPaidManually(invoiceId, paymentMethod);
      
      // Update local invoice data if onUpdateInvoice callback is provided
      if (onUpdateInvoice) {
        await onUpdateInvoice({
          ...invoiceData,
          status: 'paid',
          paidAt: new Date(),
          paymentMethod: paymentMethod,
          manuallyMarkedAsPaid: true
        });
      }
      
      toast.success(`Invoice marked as paid (${paymentMethod})`);
      setShowMarkPaidModal(false);
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    } finally {
      setIsMarkingPaid(false);
    }
  };

  // Handle both old and new invoice structures with proper error handling
  const lineItems = invoiceData.lineItems && invoiceData.lineItems.length > 0 
    ? invoiceData.lineItems 
    : [{
        id: '1',
        description: invoiceData.project || invoiceData.gig || 'Service',
        quantity: parseFloat(invoiceData.quantity) || 1,
        rate: parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount || invoiceData.price || 0),
        amount: (parseFloat(invoiceData.quantity) || 1) * (parseFloat(invoiceData.rate) || parseFloat(invoiceData.amount || invoiceData.price || 0))
      }];

  const subtotal = invoiceData.subtotal || lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  const taxAmount = invoiceData.taxAmount || 0;
  const total = invoiceData.total || subtotal + taxAmount;

  const handleDownloadPDF = () => {
    try {
      console.log('Starting PDF generation...');
      console.log('Invoice data:', invoiceData);
      console.log('Invoice ID:', invoiceId);

      // Initialize jsPDF with clean white paper settings
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true,
        floatPrecision: 16
      });
      console.log('PDF document created successfully');

      // Set default font
      doc.setFont('helvetica');
      doc.setLanguage('en-US');

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 50;
      let yPos = margin + 20;

      // Invoice Header - exactly like preview
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('INVOICE', margin, yPos);
      yPos += 50;

      // Business Information (From section) - right aligned
      const fromSectionX = pageWidth - 250;
      let fromYPos = yPos - 30; // Start higher to align with INVOICE header
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('FROM', fromSectionX, fromYPos);
      fromYPos += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(customizedData.fromName || businessInfo.businessName || invoiceData?.businessInfo?.businessName || invoiceData?.fromName || 'Your Business', fromSectionX, fromYPos);
      fromYPos += 16;
      
      const professionalTitle = businessInfo.professionalTitle || invoiceData?.businessInfo?.professionalTitle;
      if (professionalTitle) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(professionalTitle, fromSectionX, fromYPos);
        fromYPos += 14;
      }
      
      const email = businessInfo.email || invoiceData?.businessInfo?.email;
      if (email) {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(email, fromSectionX, fromYPos);
        fromYPos += 14;
      }
      
      const phone = businessInfo.phone || invoiceData?.businessInfo?.phone;
      if (phone) {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(phone, fromSectionX, fromYPos);
        fromYPos += 14;
      }
      
      const location = businessInfo.location || invoiceData?.businessInfo?.location;
      if (location) {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(location, fromSectionX, fromYPos);
        fromYPos += 14;
      }

      // Date and Invoice ID section (left side)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Date: ', margin, yPos);
      doc.setTextColor(0, 0, 0);
      doc.text(`${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })}`, margin + 30, yPos);
      yPos += 18;
      
      if (invoiceId) {
        doc.setTextColor(100, 100, 100);
        doc.text('ID: ', margin, yPos);
        doc.setTextColor(0, 0, 0);
        doc.text(`${invoiceId}`, margin + 55, yPos);
        yPos += 25;
      }

      // Status Badge - simple and clean like preview
      const statusText = invoiceData.status || 'Pending';
      const statusX = pageWidth - margin - 80;
      
      // Use appropriate colors based on status
      const status = statusText.toLowerCase();
      if (status === 'paid') {
        doc.setFillColor(220, 252, 231); // Light green background
        doc.roundedRect(statusX - 10, yPos - 35, 90, 25, 8, 8, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(21, 128, 61); // Dark green text
      } else if (status === 'overdue') {
        doc.setFillColor(254, 226, 226); // Light red background
        doc.roundedRect(statusX - 10, yPos - 35, 90, 25, 8, 8, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(153, 27, 27); // Dark red text
      } else if (status === 'draft') {
        doc.setFillColor(249, 250, 251); // Light gray background
        doc.roundedRect(statusX - 10, yPos - 35, 90, 25, 8, 8, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(75, 85, 99); // Dark gray text
      } else { // pending
        doc.setFillColor(254, 243, 199); // Light amber background
        doc.roundedRect(statusX - 10, yPos - 35, 90, 25, 8, 8, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(180, 83, 9); // Amber text
      }
      
      doc.text(statusText, statusX, yPos - 20);
      doc.setTextColor(0, 0, 0);
      yPos += 20;

      // Bill To and Project Details sections - side by side like preview
      const leftColumnX = margin;
      const rightColumnX = pageWidth / 2 + 20;
      
      // Bill To section
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('BILL TO', leftColumnX, yPos);
      yPos += 20;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(customizedData.clientName || invoiceData.client || invoiceData.customer || 'No client name', leftColumnX, yPos);
      if (invoiceData.email) {
        yPos += 16;
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(invoiceData.email, leftColumnX, yPos);
        doc.setTextColor(0, 0, 0);
      }

      // Project Details section (right column)
      const projectYPos = yPos - (invoiceData.email ? 36 : 20);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PROJECT DETAILS', rightColumnX, projectYPos);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(invoiceData.project || 'Service', rightColumnX, projectYPos + 20);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Due: ${formatDate(invoiceData.dueDate)}`, rightColumnX, projectYPos + 36);
      doc.setTextColor(0, 0, 0);

      yPos += 50;

      // Items & Services section
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('ITEMS & SERVICES', margin, yPos);
      yPos += 25;

      // Table data preparation with sub-items support
      const tableData = [];
      lineItems.forEach(item => {
        // Add main line item
        tableData.push([
          item.description || 'Service',
          (item.quantity || 1).toString(),
          `${invoiceData.currency || 'USD'} ${(parseFloat(item.rate) || 0).toFixed(2)}`,
          `${invoiceData.currency || 'USD'} ${(parseFloat(item.amount) || 0).toFixed(2)}`
        ]);
        
        // Add sub-items if they exist
        if (item.subItems && item.subItems.length > 0) {
          item.subItems.forEach(subItem => {
            tableData.push([
              `  • ${subItem.text}`, // Indented with bullet point
              '—', // Empty quantity
              '—', // Empty rate
              '—'  // Empty amount
            ]);
          });
        }
      });

      console.log('Table data prepared:', tableData);

      // Clean table styling to match preview
      autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Qty', 'Rate', 'Amount']],
        body: tableData,
        margin: { left: margin, right: margin },
        theme: 'plain',
        styles: {
          fontSize: 10,
          cellPadding: 8,
          font: 'helvetica',
          textColor: [0, 0, 0],
          lineColor: [229, 229, 229],
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 10,
          halign: 'left',
          valign: 'middle',
          cellPadding: 8,
        },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'normal' },
          1: { halign: 'center', textColor: [100, 100, 100] },
          2: { halign: 'right', textColor: [100, 100, 100] },
          3: { halign: 'right', fontStyle: 'bold' }
        },
        bodyStyles: {
          fillColor: false,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        }
      });
      
      console.log('Table added to PDF');
      
      yPos = doc.lastAutoTable.finalY + 30;

      // Summary section - right aligned like preview
      const summaryX = pageWidth - 200;
      const valueX = pageWidth - margin;

      // Subtotal
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Subtotal', summaryX, yPos);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text(`${invoiceData.currency || 'USD'} ${subtotal.toFixed(2)}`, valueX, yPos, { align: 'right' });
      yPos += 18;

      // Tax (if applicable)
      if (taxAmount > 0) {
        const taxLabel = invoiceData.taxType === 'percentage' 
          ? `Tax (${invoiceData.taxRate || 0}%)`
          : 'Tax';
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(taxLabel, summaryX, yPos);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.text(`${invoiceData.currency || 'USD'} ${taxAmount.toFixed(2)}`, valueX, yPos, { align: 'right' });
        yPos += 18;
      }

      // Add subtle line above total
      doc.setDrawColor(229, 229, 229);
      doc.setLineWidth(1);
      doc.line(summaryX, yPos + 5, valueX, yPos + 5);
      yPos += 15;

      // Total - emphasized like preview
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Total', summaryX, yPos);
      doc.setFontSize(18);
      doc.text(`${invoiceData.currency || 'USD'} ${total.toFixed(2)}`, valueX, yPos, { align: 'right' });
      yPos += 40;

      // Footer - clean and minimal like preview
      if (yPos > doc.internal.pageSize.getHeight() - 100) {
        doc.addPage();
        yPos = 50;
      }
      
      // Add subtle line above footer
      doc.setDrawColor(245, 245, 245);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 20;
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const footerText = 'Thank you for your business!';
      const footerWidth = doc.getTextWidth(footerText);
      doc.text(footerText, (pageWidth - footerWidth) / 2, yPos);
      yPos += 18;
      
      doc.setFontSize(9);
      const brandText = 'Generated with Klorah • klorah.app';
      const brandWidth = doc.getTextWidth(brandText);
      doc.setTextColor(150, 150, 150);
      doc.text('Generated with ', (pageWidth - brandWidth) / 2, yPos);
      doc.setTextColor(255, 178, 125);
      doc.text('Klorah', (pageWidth - brandWidth) / 2 + doc.getTextWidth('Generated with '), yPos);
      doc.setTextColor(150, 150, 150);
      doc.text(' • klorah.app', (pageWidth - brandWidth) / 2 + doc.getTextWidth('Generated with Klorah'), yPos);

      // Save with clean filename
      const dateStr = new Date().toISOString().split('T')[0];
      const clientName = (invoiceData.client || 'client').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const filename = invoiceId
        ? `invoice-${invoiceId}-${clientName}-${dateStr}.pdf`
        : `invoice-${clientName}-${dateStr}.pdf`;

      console.log('Saving PDF with filename:', filename);
      doc.save(filename);
      console.log('PDF saved successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#222] rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 shadow-sm"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">Invoice Preview</h2>
          <p className="text-xs md:text-sm text-gray-500 dark:text-[#666666] mt-0.5">Review your invoice before sending</p>
        </div>
        <div className="flex items-center gap-3">
          {isAuthenticated && isInvoiceOwner && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-blue-700 dark:text-blue-300">Click the edit icons to customize names</span>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Mark as Paid Icon - Only show for invoice owners when status is not already paid */}
            {isAuthenticated && isInvoiceOwner && invoiceData.status?.toLowerCase() !== 'paid' && (
              <motion.button
                onClick={() => setShowMarkPaidModal(true)}
                onMouseEnter={() => setIsHoveringMarkPaid(true)}
                onMouseLeave={() => setIsHoveringMarkPaid(false)}
                whileTap={{ scale: 0.95 }}
                disabled={isMarkingPaid}
                animate={{
                  width: isHoveringMarkPaid ? 'auto' : '32px',
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="group flex items-center justify-center overflow-hidden bg-gray-100 hover:bg-green-50 dark:bg-gray-800 dark:hover:bg-green-900/20 text-gray-600 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400 rounded-lg transition-colors duration-200 h-8"
                title={isMarkingPaid ? 'Marking as paid...' : 'Mark as paid'}
              >
                <motion.div
                  className="flex items-center gap-2 px-2"
                  animate={{
                    paddingRight: isHoveringMarkPaid ? '8px' : '0px',
                  }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {isMarkingPaid ? (
                    <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <motion.span
                    className="text-sm font-medium whitespace-nowrap"
                    animate={{
                      opacity: isHoveringMarkPaid ? 1 : 0,
                      width: isHoveringMarkPaid ? 'auto' : 0,
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    {isMarkingPaid ? 'Marking...' : 'Mark as Paid'}
                  </motion.span>
                </motion.div>
              </motion.button>
            )}
            
            <motion.button
              onClick={handleDownloadPDF}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand hover:bg-brand-hover text-black font-semibold rounded-lg transition-all duration-200 shadow-sm text-sm border border-brand/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </motion.button>
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="bg-gray-50 dark:bg-[#0D0D0D] border border-gray-100 dark:border-[#1A1A1A] rounded-xl md:rounded-2xl p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6">
        {/* Invoice Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white tracking-tight">INVOICE</h1>
            <div className="mt-2 md:mt-4 space-y-0.5 md:space-y-1">
              <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                <span className="font-medium">Date:</span> {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
              {invoiceId && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                                      <span className="font-medium">ID:</span> {invoiceId}
                </p>
              )}
            </div>
          </div>
          
          {/* Status Badge */}
          {(() => {
            const status = invoiceData.status?.toLowerCase() || 'pending';
            const getStatusStyle = () => {
              switch (status) {
                case 'paid':
                  return {
                    bg: 'bg-green-50 dark:bg-green-900/20',
                    border: 'border-green-200 dark:border-green-700/50',
                    dotColor: 'bg-green-500',
                    textColor: 'text-green-700 dark:text-green-300',
                    label: 'Paid'
                  };
                case 'overdue':
                  return {
                    bg: 'bg-red-50 dark:bg-red-900/20',
                    border: 'border-red-200 dark:border-red-700/50',
                    dotColor: 'bg-red-500',
                    textColor: 'text-red-700 dark:text-red-300',
                    label: 'Overdue'
                  };
                case 'draft':
                  return {
                    bg: 'bg-gray-50 dark:bg-gray-900/20',
                    border: 'border-gray-200 dark:border-gray-700/50',
                    dotColor: 'bg-gray-500',
                    textColor: 'text-gray-700 dark:text-gray-300',
                    label: 'Draft'
                  };
                default: // pending
                  return {
                    bg: 'bg-amber-50 dark:bg-amber-900/20',
                    border: 'border-amber-200 dark:border-amber-700/50',
                    dotColor: 'bg-amber-500',
                    textColor: 'text-amber-700 dark:text-amber-300',
                    label: 'Pending'
                  };
              }
            };
            const statusStyle = getStatusStyle();
            return (
              <div className={`flex items-center gap-2 px-3 py-1.5 ${statusStyle.bg} ${statusStyle.border} border rounded-lg`}>
                <div className={`w-1.5 h-1.5 ${statusStyle.dotColor} rounded-full ${status === 'pending' ? 'animate-pulse' : ''}`}></div>
                <span className={`text-xs font-semibold ${statusStyle.textColor}`}>{statusStyle.label}</span>
              </div>
            );
          })()}
        </div>

        {/* Business and Client Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* From Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">From</h3>
              {isAuthenticated && isInvoiceOwner && (
                <motion.button
                  onClick={() => setIsEditingFrom(!isEditingFrom)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors opacity-60 hover:opacity-100"
                  title="Customize business name"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <svg className="w-2.5 h-2.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-2.5 h-2.5 text-gray-400 dark:text-[#777777]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  )}
                </motion.button>
              )}
            </div>
            <div className="space-y-0.5 md:space-y-1">
              {isEditingFrom ? (
                <input
                  type="text"
                  value={customizedData.fromName || businessInfo.businessName || ''}
                  onChange={(e) => handleCustomizationChange('fromName', e.target.value)}
                  onBlur={async (e) => {
                    setIsEditingFrom(false);
                    await handleSaveCustomization('fromName', e.target.value);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      setIsEditingFrom(false);
                      await handleSaveCustomization('fromName', e.target.value);
                    }
                  }}
                  className="w-full px-2 py-1 text-sm md:text-base font-medium bg-transparent border border-gray-300 dark:border-[#333333] rounded text-gray-900 dark:text-white focus:outline-none focus:border-brand"
                  placeholder="Your business name"
                  autoFocus
                  disabled={isSaving}
                />
              ) : (
                <p className="font-medium text-sm md:text-base text-gray-900 dark:text-white">
                  {customizedData.fromName || businessInfo.businessName || invoiceData?.businessInfo?.businessName || invoiceData?.fromName || 'Your Business'}
                </p>
              )}
              {(businessInfo.professionalTitle || invoiceData?.businessInfo?.professionalTitle) && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                  {businessInfo.professionalTitle || invoiceData?.businessInfo?.professionalTitle}
                </p>
              )}
              {(businessInfo.email || invoiceData?.businessInfo?.email) && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                  {businessInfo.email || invoiceData?.businessInfo?.email}
                </p>
              )}
              {(businessInfo.phone || invoiceData?.businessInfo?.phone) && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                  {businessInfo.phone || invoiceData?.businessInfo?.phone}
                </p>
              )}
              {(businessInfo.location || invoiceData?.businessInfo?.location) && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                  {businessInfo.location || invoiceData?.businessInfo?.location}
                </p>
              )}
            </div>
          </div>

          {/* Bill To Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Bill To</h3>
              {isAuthenticated && isInvoiceOwner && (
                <motion.button
                  onClick={() => setIsEditingClient(!isEditingClient)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors opacity-60 hover:opacity-100"
                  title="Customize client name"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <svg className="w-2.5 h-2.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-2.5 h-2.5 text-gray-400 dark:text-[#777777]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  )}
                </motion.button>
              )}
            </div>
            <div className="space-y-0.5 md:space-y-1">
              {isEditingClient ? (
                <input
                  type="text"
                  value={customizedData.clientName || invoiceData.client || invoiceData.customer || ''}
                  onChange={(e) => handleCustomizationChange('clientName', e.target.value)}
                  onBlur={async (e) => {
                    setIsEditingClient(false);
                    await handleSaveCustomization('clientName', e.target.value);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      setIsEditingClient(false);
                      await handleSaveCustomization('clientName', e.target.value);
                    }
                  }}
                  className="w-full px-2 py-1 text-sm md:text-base font-medium bg-transparent border border-gray-300 dark:border-[#333333] rounded text-gray-900 dark:text-white focus:outline-none focus:border-brand"
                  placeholder="Client name"
                  autoFocus
                  disabled={isSaving}
                />
              ) : (
                <p className="font-medium text-sm md:text-base text-gray-900 dark:text-white">
                  {customizedData.clientName || invoiceData.client || invoiceData.customer || 'No client name'}
                </p>
              )}
              {invoiceData.email && (
                <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">{invoiceData.email}</p>
              )}
            </div>
          </div>
          
          {/* Project Details Section */}
          <div>
            <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-2">Project Details</h3>
            <div className="space-y-0.5 md:space-y-1">
              <p className="font-medium text-sm md:text-base text-gray-900 dark:text-white">{invoiceData.project || 'Service'}</p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                Due: {formatDate(invoiceData.dueDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="space-y-3">
          <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Items & Services</h3>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 dark:border-[#222]">
                  <th className="text-left py-2 md:py-3 text-xs md:text-sm font-semibold text-gray-900 dark:text-white">Description</th>
                  <th className="text-center py-2 md:py-3 text-xs md:text-sm font-semibold text-gray-900 dark:text-white w-12 md:w-20">Qty</th>
                  <th className="text-right py-2 md:py-3 text-xs md:text-sm font-semibold text-gray-900 dark:text-white w-16 md:w-24">Rate</th>
                  <th className="text-right py-2 md:py-3 text-xs md:text-sm font-semibold text-gray-900 dark:text-white w-20 md:w-28">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-[#222]">
                {lineItems.map((item, index) => {
                  const itemAmount = parseFloat(item.amount) || 0;
                  const itemRate = parseFloat(item.rate) || 0;
                  const isDiscount = itemAmount < 0;
                  
                  return (
                    <Fragment key={item.id || index}>
                      <motion.tr 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="group hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors"
                      >
                        <td className="py-2 md:py-3 text-xs md:text-sm text-gray-900 dark:text-white font-medium">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span>{item.description || 'Service'}</span>
                            {isDiscount && (
                              <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-md font-medium w-fit">
                                DISCOUNT
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 md:py-3 text-center text-xs md:text-sm text-gray-600 dark:text-[#666666]">
                          {item.quantity || 1}
                        </td>
                        <td className={`py-2 md:py-3 text-right font-mono text-xs md:text-sm ${
                          itemRate < 0 
                            ? 'text-red-600 dark:text-red-400' 
                            : 'text-gray-600 dark:text-[#666666]'
                        }`}>
                          {invoiceData.currency || 'USD'} {itemRate.toFixed(2)}
                        </td>
                        <td className={`py-2 md:py-3 text-right text-xs md:text-sm font-semibold font-mono ${
                          isDiscount 
                            ? 'text-red-600 dark:text-red-400' 
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {invoiceData.currency || 'USD'} {itemAmount.toFixed(2)}
                        </td>
                      </motion.tr>
                      {/* Sub-items for this line item */}
                      {item.subItems && item.subItems.length > 0 && item.subItems.map((subItem, subIndex) => (
                      <motion.tr
                        key={`${item.id}-sub-${subIndex}`}
                        initial={{ opacity: 0, x: -5 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (index * 0.1) + (subIndex * 0.05) + 0.1 }}
                        className="bg-gray-100/50 dark:bg-[#0A0A0A]/50"
                      >
                        <td className="py-1 md:py-1.5 pl-4 md:pl-6 text-xs text-gray-600 dark:text-[#666666]">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-brand rounded-full flex-shrink-0"></div>
                            <span>{subItem.text}</span>
                          </div>
                        </td>
                        <td className="py-1 md:py-1.5 text-center text-xs text-gray-400 dark:text-[#555555]">
                          —
                        </td>
                        <td className="py-1 md:py-1.5 text-right text-xs text-gray-400 dark:text-[#555555]">
                          —
                        </td>
                        <td className="py-1 md:py-1.5 text-right text-xs text-gray-400 dark:text-[#555555]">
                          —
                        </td>
                      </motion.tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Section */}
        <div className="flex justify-end">
          <div className="w-full max-w-sm space-y-2">
            <div className="flex justify-between items-center py-1.5">
              <span className="text-xs md:text-sm text-gray-600 dark:text-[#666666] font-medium">Subtotal</span>
              <span className="font-semibold text-xs md:text-sm text-gray-900 dark:text-white font-mono">
                {getCurrencySymbol(invoiceData.currency || 'USD')}{subtotal.toFixed(2)}
              </span>
            </div>
            
            {taxAmount > 0 && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex justify-between items-center py-1.5"
              >
                <span className="text-xs md:text-sm text-gray-600 dark:text-[#666666] font-medium">
                  {invoiceData.taxType === 'percentage' 
                    ? `Tax (${invoiceData.taxRate || 0}%)`
                    : 'Tax'
                  }
                </span>
                <span className="font-semibold text-xs md:text-sm text-gray-900 dark:text-white font-mono">
                  {getCurrencySymbol(invoiceData.currency || 'USD')}{taxAmount.toFixed(2)}
                </span>
              </motion.div>
            )}
            
            <div className="border-t-2 border-gray-200 dark:border-[#222] pt-2">
              <div className="flex justify-between items-center">
                <span className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">Total</span>
                <span className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white font-mono">
                  {getCurrencySymbol(invoiceData.currency || 'USD')}{total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 dark:border-[#222] pt-3 md:pt-4 text-center">
          <p className="text-xs md:text-sm text-gray-600 dark:text-[#666666] mb-1 md:mb-2">Thank you for your business!</p>
          <p className="text-xs text-gray-500 dark:text-[#999999]">
            Generated with <span className="text-brand font-medium">Klorah</span> • klorah.app
          </p>
        </div>
      </div>

      {/* Mark as Paid Modal */}
      {showMarkPaidModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Mark Invoice as Paid
            </h3>
            <p className="text-sm text-gray-600 dark:text-[#666666] mb-6">
              Select the payment method used for this invoice:
            </p>
            
            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleMarkAsPaid('Cash')}
                disabled={isMarkingPaid}
                className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
              >
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Cash Payment</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Paid in cash</p>
                </div>
              </button>
              
              <button
                onClick={() => handleMarkAsPaid('Check')}
                disabled={isMarkingPaid}
                className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
              >
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Check Payment</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Paid by check</p>
                </div>
              </button>
              
              <button
                onClick={() => handleMarkAsPaid('Bank Transfer')}
                disabled={isMarkingPaid}
                className="w-full flex items-center gap-3 p-3 border border-gray-200 dark:border-[#333] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
              >
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">Bank Transfer</p>
                  <p className="text-xs text-gray-500 dark:text-[#666666]">Direct bank transfer</p>
                </div>
              </button>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowMarkPaidModal(false)}
                disabled={isMarkingPaid}
                className="flex-1 px-4 py-2 border border-gray-200 dark:border-[#333] text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-[#2A2A2A] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

